import { TFunction } from 'i18next';

import { Ioption } from '../models';

/*
 * Data-driven measurement point labels/options come from the database as free text and are
 * therefore not run through i18n like the rest of the form chrome. This maps the known English
 * source strings (normalized: lowercased, single-spaced, trimmed) to keys in the
 * `measurementPoints` translation namespace so they can be translated for DISPLAY only.
 *
 * IMPORTANT: only the display text is ever translated — the select option `value` (the option ID)
 * is untouched, so the API report gate (which reads labels from the DB by ID) is unaffected.
 */
const measurementPointTextKeyMap: { [normalizedText: string]: string } = {
    'in use / not in use': 'inUseNotInUse',
    'in use/not in use': 'inUseNotInUse',
    'in use': 'inUse',
    'not in use': 'notInUse'
};

/*
 * Translate a data-driven measurement point label or option (e.g. "In Use / Not in Use") for
 * DISPLAY only. The source string comes from the database, so it is matched against
 * measurementPointTextKeyMap and resolved to a translation key. Unmapped strings are returned
 * unchanged, so this is safe to call on any label/option.
 */
export const translateMeasurementPointText = (text: string, t?: TFunction): string => {
    if (!text || !t) {
        return text;
    }
    const normalized = text.toLowerCase().replace(/\s+/g, ' ').trim();
    const key = measurementPointTextKeyMap[normalized];
    return key ? t(`measurementPoints:${key}`) : text;
};

/*
 * Translate the display labels of select options while leaving their values untouched (the value
 * is the select option ID that the API report gate keys off of).
 */
export const translateOptionLabels = (options: Ioption[], t?: TFunction): Ioption[] => {
    if (!options || !t) {
        return options;
    }
    return options.map(option => ({
        ...option,
        label: translateMeasurementPointText(option.label, t)
    }));
};
